"use client";

import { BsSearch } from "react-icons/bs";
import { Input, Checkbox, SelectSuite, Button } from "@/components/ui";
import { Slider, RatingGroup } from "@/components/ui";
import { useCropTypes } from "../_hooks/use-crops";
import { CropFilters } from "../_types";
import { useState, useEffect } from "react";
import { useDebouncedSearch } from "@/lib/hooks/use-debounced-search";
import { Fallbacks } from "@/components/ui/fallbacks";
import { useCropsContext } from "../_context";
import { certificationOptions, countryOptions, stateOptions } from "../_consts";
import { UNIT } from "@padi/lib/utils/format";
function SearchInput() {
  const { filters, updateFilter } = useCropsContext();
  const [searchInput, setSearchInput] = useState(filters.search || "");

  const { debouncedSearch } = useDebouncedSearch({
    onSearch: (value) => updateFilter("search", value),
  });

  useEffect(() => {
    setSearchInput(filters.search || "");
  }, [filters.search]);

  return (
    <div className="relative">
      <BsSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
      <Input
        type="text"
        placeholder="Search crops"
        className="pl-10"
        value={searchInput}
        onChange={(e) => {
          setSearchInput(e.target.value);
          debouncedSearch(e.target.value);
        }}
      />
    </div>
  );
}

function CropTypeFilters() {
  const { filters, toggleCropType } = useCropsContext();
  const { data: cropTypes, isLoading } = useCropTypes();

  if (isLoading) {
    return <Fallbacks.CheckboxList count={6} />;
  }

  return (
    <div className="space-y-2">
      {cropTypes?.map((type) => (
        <label key={type.id} className="flex items-center">
          <Checkbox
            checked={filters.typeId?.includes(type.id) || false}
            onCheckedChange={() => {
              toggleCropType(type.id);
            }}
          />
          <span className="ml-2 text-sm font-semibold text-muted-foreground">
            {type.name}
          </span>
        </label>
      ))}
    </div>
  );
}

function CertificationFilter() {
  const { filters, updateFilter } = useCropsContext();

  return (
    <SelectSuite
      placeholder="All Certificates"
      options={certificationOptions}
      value={filters.certificationType || "all"}
      onValueChange={(value) => {
        updateFilter(
          "certificationType",
          value === "all"
            ? undefined
            : (value as CropFilters["certificationType"]),
        );
      }}
    />
  );
}

function LocationFilters() {
  const { filters, updateFilter } = useCropsContext();

  return (
    <div className="space-y-3">
      <SelectSuite
        placeholder="All Countries"
        options={countryOptions}
        value={filters.country || "all"}
        onValueChange={(value) => {
          updateFilter("country", value);
        }}
      />
      <SelectSuite
        placeholder="All State/Regions"
        options={stateOptions(filters.country || "nigeria")}
        value={filters.state || "all"}
        onValueChange={(value) => {
          updateFilter("state", value);
        }}
      />
    </div>
  );
}

function PriceRangeFilter() {
  const { filters, setPriceRange } = useCropsContext();

  const DEFAULT_PRICE_RANGE = [0, 15000000, 100000];

  const formatPrice = (price: number) => {
    if (price >= 1000000) {
      return `$${(price / 1000000).toFixed(1)}M`;
    }
    if (price >= 1000) {
      return `$${(price / 1000).toFixed(0)}K`;
    }
    return `$${price}`;
  };

  const priceRange = [
    filters.priceFrom || DEFAULT_PRICE_RANGE[0],
    filters.priceTo || DEFAULT_PRICE_RANGE[1],
  ];

  const handlePriceRangeChange = (range: number[]) => {
    setPriceRange(range[0], range[1]);
  };

  return (
    <div className="space-y-4">
      <Slider
        value={priceRange}
        onValueChange={handlePriceRangeChange}
        min={DEFAULT_PRICE_RANGE[0]}
        max={DEFAULT_PRICE_RANGE[1]}
        step={DEFAULT_PRICE_RANGE[2]}
        className="w-full"
      />
      <div className="flex justify-between text-sm text-muted-foreground">
        <span>{formatPrice(priceRange[0])}</span>
        <span>{formatPrice(priceRange[1])}</span>
      </div>
      <div className="flex items-center justify-between">
        <div className="flex flex-col">
          <label className="text-foreground">Min ($)</label>
          <Input
            type="number"
            placeholder="Min ($)"
            value={priceRange[0]}
            onChange={(e) => {
              const newRange = [Number(e.target.value), priceRange[1]];
              handlePriceRangeChange(newRange);
            }}
            min={DEFAULT_PRICE_RANGE[0]}
            max={priceRange[1]}
            step={DEFAULT_PRICE_RANGE[2]}
          />
        </div>
        <div className="flex flex-col">
          <label className="text-foreground">Max ($)</label>
          <Input
            type="number"
            placeholder="Max ($)"
            value={priceRange[1]}
            onChange={(e) => {
              const newRange = [priceRange[0], Number(e.target.value)];
              handlePriceRangeChange(newRange);
            }}
            min={priceRange[0]}
            max={DEFAULT_PRICE_RANGE[1]}
            step={DEFAULT_PRICE_RANGE[2]}
          />
        </div>
      </div>
    </div>
  );
}

function RatingFilter() {
  const { filters, updateFilter } = useCropsContext();
  const ratings = [5, 4, 3, 2, 1];

  return (
    <RatingGroup
      options={ratings}
      name="rating"
      value={filters.sellerRatingFrom || 1}
      onChange={(value) => {
        updateFilter("sellerRatingFrom", value);
      }}
    />
  );
}

function ResetButton() {
  const { resetFilters } = useCropsContext();

  return (
    <Button variant="accent" size="lg" className="w-48" onClick={resetFilters}>
      Reset
    </Button>
  );
}

export function FiltersSidebar() {
  const filterSections = [
    {
      id: "search",
      title: "Filter Products",
      component: <SearchInput />,
    },
    {
      id: "crop-type",
      title: "Crop Type",
      component: <CropTypeFilters />,
    },
    {
      id: "certification",
      title: "Certification",
      component: <CertificationFilter />,
    },
    {
      id: "location",
      title: "Location",
      component: <LocationFilters />,
    },
    {
      id: "price-range",
      title: `Price Range (per ${UNIT})`,
      component: <PriceRangeFilter />,
    },
    {
      id: "seller-rating",
      title: "Seller Rating",
      component: <RatingFilter />,
    },
  ];

  return (
    <div className="bg-white h-full">
      {filterSections.map((section) => (
        <div key={section.id} className="mb-6">
          {section.title && (
            <h4 className="font-semibold text-primary mb-3">{section.title}</h4>
          )}
          {section.component}
        </div>
      ))}
      <div className="flex flex-col gap-4">
        <ResetButton />
      </div>
    </div>
  );
}
